import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// ─── CSS BUILDER (mirror of builder) ─────────────────────────────────────────
const buildStyle = (comp, extra = {}) => {
  const borderRadius = `${comp.borderTopLeftRadius || 0}px ${comp.borderTopRightRadius || 0}px ${comp.borderBottomRightRadius || 0}px ${comp.borderBottomLeftRadius || 0}px`;
  const padding = `${comp.paddingTop || 0}px ${comp.paddingRight || 0}px ${comp.paddingBottom || 0}px ${comp.paddingLeft || 0}px`;
  const margin  = `${comp.marginTop || 0}px ${comp.marginRight || 0}px ${comp.marginBottom || 0}px ${comp.marginLeft || 0}px`;

  const bs = comp.borderStyle || 'solid';
  const bc = comp.borderColor || 'transparent';

  return {
    position: comp.position || 'absolute',
    top: comp.top,
    left: comp.left,
    width: comp.width,
    height: comp.height,
    backgroundColor: comp.backgroundColor,
    color: comp.color,
    opacity: comp.opacity,
    fontSize: comp.fontSize,
    fontFamily: comp.fontFamily,
    fontWeight: comp.fontWeight,
    lineHeight: comp.lineHeight,
    letterSpacing: comp.letterSpacing ? `${comp.letterSpacing}px` : undefined,
    textAlign: comp.textAlign,
    textDecoration: comp.textDecoration,
    fontStyle: comp.fontStyle,
    borderTop:    `${comp.borderTopWidth    || 0}px ${bs} ${bc}`,
    borderRight:  `${comp.borderRightWidth  || 0}px ${bs} ${bc}`,
    borderBottom: `${comp.borderBottomWidth || 0}px ${bs} ${bc}`,
    borderLeft:   `${comp.borderLeftWidth   || 0}px ${bs} ${bc}`,
    borderRadius,
    padding,
    margin,
    boxShadow: comp.boxShadow,
    display: comp.display,
    flexDirection: comp.flexDirection,
    justifyContent: comp.justifyContent,
    alignItems: comp.alignItems,
    flexWrap: comp.flexWrap,
    gap: comp.gap ? `${comp.gap}px` : 0,
    overflow: comp.overflow,
    cursor: comp.cursor,
    zIndex: comp.zIndex,
    visibility: comp.visibility,
    boxSizing: 'border-box',
    ...extra,
  };
};

// ─── SCREEN VIEWER ───────────────────────────────────────────────────────────
const ScreenViewer = ({ configId, config: externalConfig, functionRegistry = {} }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [compStates, setCompStates] = useState({});

  useEffect(() => {
    if (externalConfig) {
      setConfig(externalConfig);
      setLoading(false);
    } else if (configId) {
      try {
        const saved = localStorage.getItem(`screen_${configId}`);
        if (saved) { setConfig(JSON.parse(saved)); }
        else setError('Screen configuration not found: ' + configId);
      } catch (err) {
        setError('Error loading: ' + err.message);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [configId, externalConfig]);

  const updateState = (id, patch) =>
    setCompStates(p => ({ ...p, [id]: { ...p[id], ...patch } }));

  if (loading) return <Centered text="Loading…" />;
  if (error)   return <Centered text={error} color="#f87171" />;
  if (!config) return <Centered text="No configuration loaded" color="#64748b" />;

  return (
    <div style={{ width: config.width || '100%', height: config.height || '100vh', backgroundColor: config.bg || config.backgroundColor || '#fff', position: 'relative', overflow: 'auto' }}>
      {(config.components || []).map(comp => (
        <RuntimeComp
          key={comp.id}
          comp={comp}
          state={compStates[comp.id] || {}}
          onStateChange={patch => updateState(comp.id, patch)}
          functionRegistry={functionRegistry}
        />
      ))}
    </div>
  );
};

ScreenViewer.propTypes = {
  configId: PropTypes.string,
  config: PropTypes.object,
  functionRegistry: PropTypes.object,
};

// ─── RUNTIME COMPONENT ───────────────────────────────────────────────────────
const RuntimeComp = ({ comp, state, onStateChange, functionRegistry }) => {
  const [hovered, setHovered] = useState(false);

  const style = buildStyle(comp, {
    ...(hovered && comp.hoverBg ? { backgroundColor: comp.hoverBg } : {}),
  });

  const handleClick = () => {
    const cfg = comp.onClick;
    if (!cfg) return;
    if (cfg.type === 'function' && cfg.functionName) {
      const fn = functionRegistry[cfg.functionName];
      if (fn) fn(cfg.params);
      else console.warn('[ScreenViewer] Function not in registry:', cfg.functionName);
    } else if (cfg.type === 'api') {
      fetch(cfg.endpoint, { method: cfg.method || 'GET', headers: cfg.headers || {}, body: cfg.body ? JSON.stringify(cfg.body) : undefined })
        .then(r => r.json())
        .then(data => onStateChange({ apiData: data }))
        .catch(err => console.error('[ScreenViewer] API call failed:', err));
    }
  };

  const commonProps = {
    style,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onClick: handleClick,
  };

  switch (comp.type) {
    case 'row':
    case 'container':
      return (
        <div {...commonProps}>
          {(comp.children || []).map(child => (
            <RuntimeComp key={child.id} comp={child} state={state[child.id] || {}} onStateChange={s => onStateChange({ [child.id]: s })} functionRegistry={functionRegistry} />
          ))}
        </div>
      );

    case 'button':
      return <button {...commonProps} style={{ ...style, appearance: 'none', outline: 'none', fontFamily: comp.fontFamily }}>{comp.textContent || 'Button'}</button>;

    case 'text':
      return <div {...commonProps} style={{ ...style, whiteSpace: 'pre-wrap' }}>{comp.textContent || ''}</div>;

    case 'input':
      return (
        <input
          {...commonProps}
          type={comp.inputType || 'text'}
          placeholder={comp.placeholder}
          name={comp.name}
          required={comp.required}
          value={state.value || ''}
          onChange={e => onStateChange({ value: e.target.value })}
          style={{ ...style, fontFamily: comp.fontFamily }}
        />
      );

    case 'image':
      return <img {...commonProps} src={comp.src} alt={comp.alt} style={{ ...style, objectFit: comp.objectFit || 'cover' }} />;

    case 'table':
      return <TableComp comp={comp} style={style} />;

    case 'chart':
      return <ChartComp comp={comp} style={style} />;

    case 'custom_html':
      return <div {...commonProps} dangerouslySetInnerHTML={{ __html: comp.htmlContent }} />;

    default:
      return <div {...commonProps} style={{ ...style, color: '#999', fontSize: 12 }}>Unknown: {comp.type}</div>;
  }
};

RuntimeComp.propTypes = {
  comp: PropTypes.object.isRequired,
  state: PropTypes.object,
  onStateChange: PropTypes.func.isRequired,
  functionRegistry: PropTypes.object,
};

// ─── TABLE ───────────────────────────────────────────────────────────────────
const TableComp = ({ comp, style }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (comp.dataSource?.endpoint) {
      setLoading(true);
      fetch(comp.dataSource.endpoint, { method: comp.dataSource.method || 'GET' })
        .then(r => r.json())
        .then(d => { setRows(Array.isArray(d) ? d : d.data || []); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [comp.dataSource]);

  return (
    <div style={style}>
      {loading && <div style={{ padding: 16, color: '#999', textAlign: 'center' }}>Loading…</div>}
      {!loading && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {(comp.columns || []).map((col, i) => (
                <th key={i} style={{ padding: comp.cellPadding ?? 10, backgroundColor: comp.headerBg || '#f8f9fa', color: comp.headerColor || '#333', fontWeight: comp.headerFontWeight || 600, textAlign: 'left', borderBottom: '2px solid #dee2e6', width: col.width }}>
                  {col.label || col.field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={(comp.columns || []).length} style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>No data</td></tr>
            ) : rows.map((row, ri) => (
              <tr key={ri} style={{ backgroundColor: comp.rowStriped && ri % 2 === 1 ? (comp.stripedColor || '#f8f9fa') : undefined }}>
                {(comp.columns || []).map((col, ci) => (
                  <td key={ci} style={{ padding: comp.cellPadding ?? 10, borderBottom: '1px solid #eee' }}>{row[col.field]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

TableComp.propTypes = { comp: PropTypes.object.isRequired, style: PropTypes.object };

// ─── CHART ───────────────────────────────────────────────────────────────────
const ChartComp = ({ comp, style }) => {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (comp.dataSource?.endpoint) {
      fetch(comp.dataSource.endpoint, { method: comp.dataSource.method || 'GET' })
        .then(r => r.json())
        .then(d => setData(d))
        .catch(err => console.error('Chart fetch error:', err));
    } else {
      // demo data when no endpoint
      setData({
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [{ label: comp.chartTitle || 'Dataset', data: [42, 68, 53, 85, 71, 93] }],
      });
    }
  }, [comp.dataSource]);

  useEffect(() => {
    if (!canvasRef.current || !data) return;

    const colors = (comp.chartColors || '#5b7cf6,#34d399,#fbbf24,#f87171').split(',').map(c => c.trim());

    const init = () => {
      if (chartRef.current) chartRef.current.destroy();
      const ctx = canvasRef.current.getContext('2d');

      const datasets = (data.datasets || [{ label: comp.chartTitle || 'Dataset', data: data.values || [] }]).map((ds, i) => ({
        ...ds,
        backgroundColor: colors[i % colors.length] + (comp.chartType === 'line' ? '33' : 'cc'),
        borderColor: colors[i % colors.length],
        borderWidth: 2,
        fill: comp.chartType === 'area',
        tension: 0.3,
        pointRadius: 4,
      }));

      chartRef.current = new window.Chart(ctx, {
        type: comp.chartType === 'area' ? 'line' : comp.chartType || 'bar',
        data: { labels: data.labels || [], datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: comp.showLegend !== false },
            title: { display: !!(comp.chartTitle), text: comp.chartTitle || '' },
          },
          scales: ['pie', 'doughnut', 'polarArea', 'radar'].includes(comp.chartType) ? {} : {
            x: { display: true, grid: { display: comp.showGrid !== false }, title: { display: !!(comp.xAxisLabel), text: comp.xAxisLabel || '' } },
            y: { display: true, beginAtZero: true, grid: { display: comp.showGrid !== false }, title: { display: !!(comp.yAxisLabel), text: comp.yAxisLabel || '' } },
          },
        },
      });
    };

    if (typeof window.Chart !== 'undefined') {
      init();
    } else {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
      s.onload = init;
      document.head.appendChild(s);
    }

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [data, comp.chartType, comp.chartTitle, comp.xAxisLabel, comp.yAxisLabel, comp.showLegend, comp.showGrid, comp.chartColors]);

  return (
    <div style={style}>
      {!data ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aaa', fontSize: 12 }}>Loading chart…</div>
      ) : (
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
      )}
    </div>
  );
};

ChartComp.propTypes = { comp: PropTypes.object.isRequired, style: PropTypes.object };

// ─── SCREEN VIEWER SHELL (example) ───────────────────────────────────────────
export const ScreenViewerExample = () => {
  const [configId, setConfigId] = useState('');
  const [mode, setMode] = useState('input');

  if (mode === 'input') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 16, fontFamily: 'system-ui', background: '#0f1117', color: '#e2e8f0' }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Screen Viewer</div>
        <input
          type="text"
          value={configId}
          onChange={e => setConfigId(e.target.value)}
          placeholder="Enter Screen ID"
          style={{ padding: '10px 14px', fontSize: 14, border: '1px solid #2a2d3a', borderRadius: 6, background: '#1a1d27', color: '#e2e8f0', width: 280 }}
        />
        <button
          onClick={() => setMode('view')}
          disabled={!configId}
          style={{ padding: '10px 24px', fontSize: 14, background: '#5b7cf6', color: '#fff', border: 'none', borderRadius: 6, cursor: configId ? 'pointer' : 'not-allowed', opacity: configId ? 1 : 0.5 }}
        >
          Load Screen
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ padding: '8px 16px', background: '#1a1d27', borderBottom: '1px solid #2a2d3a' }}>
        <button onClick={() => setMode('input')} style={{ padding: '6px 12px', background: 'none', border: '1px solid #2a2d3a', borderRadius: 4, color: '#e2e8f0', cursor: 'pointer', fontFamily: 'system-ui', fontSize: 12 }}>
          ← Back
        </button>
      </div>
      <ScreenViewer configId={configId} />
    </div>
  );
};

const Centered = ({ text, color }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: 14, color: color || '#666' }}>{text}</div>
);

export { ScreenViewer };
export default ScreenViewer;